import * as React from "react";

import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import { redirect } from "@remix-run/node";
import type { MetaFunction } from "@remix-run/react";
import {
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
  useSearchParams,
} from "@remix-run/react";
import { withZod } from "@remix-validated-form/with-zod";
import lookup from "country-code-lookup";
import type { Countries } from "country-to-currency";
import type { CountryCode } from "libphonenumber-js";
import { parsePhoneNumber } from "libphonenumber-js/max";
import { isValidPhoneNumber } from "libphonenumber-js/min";
import {
  useFormContext,
  ValidatedForm,
  validationError,
} from "remix-validated-form";
import { z } from "zod";

import DashInput, {
  dashInputClassname,
} from "~/components/dashboard/DashInput/DashInput";
import DashSubmitButton from "~/components/dashboard/DashSubmitButton/DashSubmitButton";
import LoginCard from "~/components/dashboard/LoginCard/LoginCard";
import {
  APP_PAGE_URL,
  COUNTRIES,
  DEFAULT_COUNTRY_ISO,
  DEFAULT_TRIAL_DAYS,
  LOGIN_URL,
  MAINTENANCE_MODE,
  MAINTENANCE_URL,
} from "~/Config/config";
import { stripe } from "~/integrations/stripe";
import { supabaseServerClient } from "~/integrations/supabase/server";
import { isUnauthenticated } from "~/modules/auth/session.server";
import { stripeCheckout } from "~/modules/stripe/stripe.server";
import { createUserAccount, getRejectedPasswordMessage } from "~/modules/user";
import { isFormProcessing } from "~/utils";
import { SupaStripeStackError } from "~/utils/error";
import { response } from "~/utils/http.server";

export async function loader({ request }: LoaderFunctionArgs) {
  try {
    if (MAINTENANCE_MODE) return redirect(MAINTENANCE_URL);

    const userIsUnauthenticated = await isUnauthenticated(request);

    if (!userIsUnauthenticated) {
      return response.redirect(APP_PAGE_URL, { authSession: null });
    }

    const url = new URL(request.url);
    const priceId = url.searchParams.get("id");

    if (!priceId) return redirect(APP_PAGE_URL);

    return response.ok({ priceId }, { authSession: null });
  } catch (cause) {
    throw response.error(cause, { authSession: null });
  }
}

const JoinFormSchema = z
  .object({
    email: z
      .string()
      .email("Please enter a valid email address")
      .transform((email) => email.toLowerCase()),
    password: z.string().min(8, "Password needs to be at least 8 characters"),
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    country: z.string(),
    phone: z.string({ required_error: "Phone number is required" }),
    promoCode: z.string().trim().toUpperCase().optional(),
    agency: z.string(),
    priceId: z.string(),
  })
  .transform((value, ctx) => {
    const phone = value.phone;
    const country = value.country.toUpperCase() as CountryCode;
    const countryString = lookup.byIso(country)?.country;
    if (!isValidPhoneNumber(phone, country)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          phone || phone !== "null"
            ? `Phone number provided is not a valid phone number for ${countryString}`
            : "Phone number is required",
        path: ["phone"],
      });
      return z.NEVER;
    }

    const parsedNumber = parsePhoneNumber(phone, country);

    if (
      !(
        parsedNumber.getType() === "MOBILE" ||
        parsedNumber.getType() == "FIXED_LINE_OR_MOBILE"
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `This is not a mobile phone for ${countryString}`,
        path: ["phone"],
      });
      return z.NEVER;
    }

    return { ...value, phone: parsedNumber.number as string };
  });

const JoinFormSchemaValidator = withZod(JoinFormSchema);

const formId = "JOIN_FORM";

export async function action({ request }: ActionFunctionArgs) {
  try {
    if (MAINTENANCE_MODE) return redirect(MAINTENANCE_URL);

    const serverValidator = withZod(
      JoinFormSchema.transform(async (data, ctx) => {
        if (data.promoCode) {
          // @TODO: Remove this when we have a proper way to handle promo codes
          // Apply a different promo code if the subscription is quarterly
          const isQuarterly = await stripe.prices
            .retrieve(data.priceId)
            .then(
              (price) =>
                price.recurring &&
                price.recurring.interval === "month" &&
                price.recurring.interval_count === 3
            );

          // Custom logic to apply a promo code for quarterly subscriptions when entering the promo code NZREC
          // Stripe doesn't do well with quaterly promo code discounts for (2 months free for example)
          const promotionsCode = await stripe.promotionCodes.list({
            code:
              data.promoCode.toUpperCase() === "NZREC" && isQuarterly
                ? "NZRECQUARTER"
                : data.promoCode,
            limit: 1,
          });

          if (promotionsCode.data.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Invalid promo code, please try again or contact us",
              fatal: true,
              path: ["promoCode"],
            });

            return z.NEVER;
          }

          return { ...data, promoCode: promotionsCode.data?.[0].id };
        }

        return data;
      })
    );

    const result = await serverValidator.validate(await request.formData());

    if (result.error) return validationError(result.error);

    const {
      email,
      password,
      lastName,
      firstName,
      priceId,
      country,
      agency,
      phone,
      promoCode,
    } = result.data;

    const { user } = await createUserAccount({
      email,
      password,
      firstName,
      lastName,
      country,
      agency,
      phoneNumber: phone,
    });

    if (!user?.customer_id) {
      throw new SupaStripeStackError({
        message: "Unable to create acccount or customer ID",
        status: 403,
        metadata: { email },
      });
    }

    // Sign in the user to create a proper browser session
    const { supabase, headers } = await supabaseServerClient(request);

    const {
      data: { session },
      error: signInError,
    } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError || !session) {
      throw new SupaStripeStackError({
        message: "Unable to sign in user after account creation",
        status: 403,
        metadata: { email },
      });
    }

    // Once a customer has subscribed, we can't change their currency.
    // Be sure to be consistent with the currency you use for your prices.
    // If there is a mismatch, the customer will be unable to checkout again.
    // It's an edge case, but it's good to be aware of.

    const url = await stripeCheckout(
      {
        customerId: user.customer_id,
        priceId,
        trialPeriodDays: DEFAULT_TRIAL_DAYS || undefined, // TODO: make this configurable
        country: country as Countries,
        promoCode,
      },
      request.url
    );

    return response.redirect(url, {
      authSession: null,
      headers,
    });
  } catch (cause) {
    // The agent can fix a rejected password, so say why on the field instead
    // of showing the generic signup error.
    const rejectedPasswordMessage = getRejectedPasswordMessage(cause);
    if (rejectedPasswordMessage) {
      return validationError({
        formId,
        fieldErrors: { password: rejectedPasswordMessage },
      });
    }

    return response.error(cause, { authSession: null });
  }
}

export const meta: MetaFunction = () => [
  {
    title: "Join us - Konnect",
  },
];

export default function Join() {
  const { priceId } = useLoaderData<typeof loader>();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const disabled = isFormProcessing(navigation.state);
  const actionData = useActionData<typeof action>();
  const { fieldErrors } = useFormContext(formId);

  return (
    <LoginCard title="Sign up">
      <ValidatedForm
        id={formId}
        validator={JoinFormSchemaValidator}
        method="post"
        className="space-y-6"
      >
        <DashInput
          label="First Name*"
          name={"firstName"}
          tabIndex={1}
          disabled={disabled}
          classNameContainer="mt-2"
        />
        <DashInput
          label="Last Name*"
          name={"lastName"}
          tabIndex={2}
          disabled={disabled}
          classNameContainer="mt-2"
        />
        <DashInput
          label="Real Estate Agency*"
          name={"agency"}
          tabIndex={3}
          disabled={disabled}
          classNameContainer="mt-2"
        />
        <div className="mt-2">
          <label
            className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-200"
            htmlFor={"country"}
          >
            Country*
          </label>
          <select
            data-testid="country"
            name={"country"}
            required
            disabled={disabled}
            tabIndex={4}
            defaultValue={DEFAULT_COUNTRY_ISO}
            className={dashInputClassname}
          >
            {COUNTRIES.map((country) => (
              <option key={country.iso2} value={country.iso2}>
                {country.country}
              </option>
            ))}
          </select>
          {fieldErrors.country && (
            <span className="pt-2 text-xs text-red-700" id="email-error">
              {fieldErrors.country}
            </span>
          )}
        </div>
        <DashInput
          label="Email Address*"
          name={"email"}
          type="email"
          tabIndex={5}
          classNameContainer="mt-2"
        />
        <DashInput
          label="Mobile phone*"
          name={"phone"}
          tabIndex={6}
          classNameContainer="mt-2"
        />
        <DashInput
          label="Password*"
          name={"password"}
          type="password"
          tabIndex={7}
          classNameContainer="mt-2"
        />
        <DashInput label="Promo code" name={"promoCode"} tabIndex={8} />
        <div className="mt-2 text-sm text-gray-500">
          By creating an account, you agree to our Terms.
        </div>
        <input type="hidden" name={"priceId"} value={priceId} />
        {actionData && "error" in actionData && (
          <div className="pt-1 text-red-700" id="join-error">
            {actionData?.error?.message}
          </div>
        )}
        <DashSubmitButton tabIndex={9} />
      </ValidatedForm>
      <div className="mt-4 flex items-center justify-center">
        <div className="text-center text-sm text-gray-500">
          {"Already have an account? "}
          <Link
            className="underline"
            to={{
              pathname: LOGIN_URL,
              search: searchParams.toString(),
            }}
          >
            Log in
          </Link>
        </div>
      </div>
    </LoginCard>
  );
}
